/**
 * SOLID Review
 * S — Strong. One concern: what the `ClientDecisions` socket answers while nothing is connected to it.
 * O — Strong. A contract op added later fails the contract-coverage test, not a cast.
 * L — Strong. Implements `IPortClientDecisions` exactly: every op, the same answer.
 * I — Strong. The contract's ops and nothing else.
 * D — Strong. The kernel's failure builder and the port's interface.
 */
import { portFailure } from '@ml3d/kernel';
import type { IPortClientDecisions } from '../../domain/ports/ClientDecisions.contract';

/**
 * THE `ClientDecisions` SOCKET'S NULL OBJECT (frontend-application-oo P6, 2026-10-28; `unavailable()` built it reflectively until
 * then). The frontend's failure policy (strict-ports FE1): a socket the assembly never connected degrades the move that needed
 * it — every op answers `UNAVAILABLE` — and the rest of the app still renders. The kernel guards it on the same terms as a real
 * provider, which is why it answers in the contract's own Result shape. The production-wiring test, not this, keeps an
 * unconnected socket from shipping: it asserts `assertAssembled` with an empty allowlist.
 */
export class UnavailableClientDecisions implements IPortClientDecisions {
  private readonly failure = portFailure('UNAVAILABLE', 'ClientDecisions is not connected.');

  async roundStarted() {
    return this.failure;
  }

  async claimAnnounced() {
    return this.failure;
  }

  async cupLifted() {
    return this.failure;
  }

  async diceRolled() {
    return this.failure;
  }

  async roomUpdated() {
    return this.failure;
  }

  async playerSuspended() {
    return this.failure;
  }

  async playerResumed() {
    return this.failure;
  }

  async roomAbandoned() {
    return this.failure;
  }

  async decisionCountdown() {
    return this.failure;
  }

  async tableView() {
    return this.failure;
  }

  async connectivity() {
    return this.failure;
  }

  async planResync() {
    return this.failure;
  }

  async activeRoundRestored() {
    return this.failure;
  }

  async suspendedReconciled() {
    return this.failure;
  }

  async planLobbyRead() {
    return this.failure;
  }

  async lobbyActiveRound() {
    return this.failure;
  }

  async lobbyRoomLoaded() {
    return this.failure;
  }

  async planStartGame() {
    return this.failure;
  }

  async gameStarted() {
    return this.failure;
  }

  async planRoll() {
    return this.failure;
  }

  async rolled() {
    return this.failure;
  }

  async planPeek() {
    return this.failure;
  }

  async peeked() {
    return this.failure;
  }

  async planAnnounce() {
    return this.failure;
  }

  async claimAccepted() {
    return this.failure;
  }

  async planBelieve() {
    return this.failure;
  }

  async believeAccepted() {
    return this.failure;
  }

  async planRollFirst() {
    return this.failure;
  }

  async rollFirstAccepted() {
    return this.failure;
  }

  async planLift() {
    return this.failure;
  }

  async liftAnswered() {
    return this.failure;
  }

  async planPlayAgain() {
    return this.failure;
  }

  async playAgainAnswered() {
    return this.failure;
  }

  async leaveTable() {
    return this.failure;
  }

  async sessionStarted() {
    return this.failure;
  }

  async planEntry() {
    return this.failure;
  }

  async meAnswered() {
    return this.failure;
  }

  async meUnreachable() {
    return this.failure;
  }

  async resumeValidated() {
    return this.failure;
  }

  async planRestore() {
    return this.failure;
  }

  async restoreJoined() {
    return this.failure;
  }

  async roundRestored() {
    return this.failure;
  }

  async planIntent() {
    return this.failure;
  }

  async entryFinished() {
    return this.failure;
  }

  async planJoin() {
    return this.failure;
  }

  async joinRouted() {
    return this.failure;
  }

  async tableJoined() {
    return this.failure;
  }

  async tableRejoined() {
    return this.failure;
  }

  async tableLateJoined() {
    return this.failure;
  }

  async planHost() {
    return this.failure;
  }

  async tableHosted() {
    return this.failure;
  }

  async planColdRejoin() {
    return this.failure;
  }

  async activeRoomFound() {
    return this.failure;
  }

  async planGuest() {
    return this.failure;
  }

  async guestCreated() {
    return this.failure;
  }

  async planTableCode() {
    return this.failure;
  }

  async tableCodeChecked() {
    return this.failure;
  }

  async navigate() {
    return this.failure;
  }

  async dismissJoinError() {
    return this.failure;
  }

  async planLoginCode() {
    return this.failure;
  }

  async loginCodeAnswered() {
    return this.failure;
  }

  async planSignUp() {
    return this.failure;
  }

  async signedUp() {
    return this.failure;
  }

  async planVerifyCode() {
    return this.failure;
  }

  async signedIn() {
    return this.failure;
  }

  async profileLoaded() {
    return this.failure;
  }

  async planNickname() {
    return this.failure;
  }

  async nicknameChanged() {
    return this.failure;
  }

  async planEmailChange() {
    return this.failure;
  }

  async planVerifyEmailChange() {
    return this.failure;
  }

  async emailChanged() {
    return this.failure;
  }

  async signedOut() {
    return this.failure;
  }

  async planLegalReview() {
    return this.failure;
  }

  async legalReviewOf() {
    return this.failure;
  }

  async planLegalAcceptance() {
    return this.failure;
  }

  async consentChanged() {
    return this.failure;
  }

  async planCookieConsentRecord() {
    return this.failure;
  }

  async creditsChanged() {
    return this.failure;
  }

  async balanceRead() {
    return this.failure;
  }

  async planAccountWatch() {
    return this.failure;
  }

  async planAdConfig() {
    return this.failure;
  }

  async adConfigLoaded() {
    return this.failure;
  }

  async mediaToggled() {
    return this.failure;
  }

  async mediaReconnect() {
    return this.failure;
  }

  async mediaIdleChecked() {
    return this.failure;
  }

  async mediaRemovedByServer() {
    return this.failure;
  }

  async mediaBannerDismissed() {
    return this.failure;
  }

  async planPresence() {
    return this.failure;
  }

  async planClientError() {
    return this.failure;
  }

  async diceRankingLoaded() {
    return this.failure;
  }
}
